import { ApplicationInsights } from '@microsoft/applicationinsights-web';

class AppInsightsService {
    constructor() {
        this.appInsights = null;
        this.isInitialized = false;
    }

    initialize(connectionString) {
        if (this.isInitialized) {
            console.warn('⚠️ Application Insights is already initialized - skipping');
            return;
        }
        
        if (!connectionString) {
            console.warn('⚠️ Application Insights connection string not provided. Telemetry will be disabled.');
            return;
        }

        try {
            this.appInsights = new ApplicationInsights({
                config: {
                    connectionString: connectionString,
                    enableAutoRouteTracking: true,           
                    enableUnhandledPromiseRejectionTracking: true, 
                    autoTrackPageVisitTime: true,          
                    enableRequestHeaderTracking: false,
                    enableResponseHeaderTracking: false,
                    enableCorsCorrelation: false,
                    disableFetchTracking: true,
                    disableAjaxTracking: true,
                    disableExceptionTracking: false,
                }
            });
            
            this.appInsights.loadAppInsights();
            this.isInitialized = true;
            console.log('Application Insights initialized successfully');
        } catch (error) {
            console.error('Failed to initialize Application Insights:', error);
            this.isInitialized = false;
        }
    }

    trackEvent(event) {
        if (!this.isInitialized || !this.appInsights) {
            console.warn('Application Insights not initialized. Event not tracked:', event.name);
            return;
        }

        try {
            this.appInsights.trackEvent(
                { 
                    name: event.name,
                    properties: event.properties,
                    measurements: event.measurements
                }
            );
            console.log(`️✅ Telemetry tracked: ${event.name}`, event.properties);
        } catch (error) {
            console.error('❌ Failed to track event:', error);
        }
    }

    trackException(error, properties) {
        if (!this.isInitialized || !this.appInsights) {
            console.warn('Application Insights not initialized. Exception not tracked');
            return;
        }

        try {
            const exception = error instanceof Error ? error : new Error(String(error));
            this.appInsights.trackException({ exception }, properties);
            console.log(`️✅ Exception tracked: ${exception.message}`);
        } catch (err) {
            console.error('❌ Failed to track exception:', err);
        }
    }

    trackPageView(name, uri, properties) {
        if (!this.isInitialized || !this.appInsights) {
            console.warn('Application Insights not initialized. Page view not tracked');
            return;
        }

        try {
            this.appInsights.trackPageView({ 
                name, 
                uri,
                properties 
            });
            console.log(`✅ Page view tracked: ${name || uri || 'unknown'}`);
        } catch (error) {
            console.error('❌ Failed to track page view:', error);
        }
    }

    trackMetric(name, average, properties) {
        if (!this.isInitialized || !this.appInsights) {
            console.warn('Application Insights not initialized. Metric not tracked:', name);
            return;
        }

        try {
            this.appInsights.trackMetric({ name, average }, properties);
            console.log(`✅ Metric tracked: ${name} = ${average}`);
        } catch (error) {
            console.error('❌ Failed to track metric:', error);
        }
    }

    trackTrace(message, properties) {
        if (!this.isInitialized || !this.appInsights) {
            console.warn('Application Insights not initialized. Trace not tracked');
            return;
        }

        try {
            this.appInsights.trackTrace({ message }, properties);
            console.log(`✅ Trace tracked: ${message}`);
        } catch (error) {
            console.error('❌ Failed to track trace:', error);
        }
    }

    trackDependency(options) {
        if (!this.isInitialized || !this.appInsights) {
            console.warn('Application Insights not initialized. Dependency not tracked');
            return;
        }

        try {
            this.appInsights.trackDependencyData({
                id: `${options.name}_${Date.now()}`,
                target: options.name,
                type: options.dependencyTypeName,
                name: options.name,
                data: options.data,
                duration: options.duration,
                success: options.success,
                responseCode: options.resultCode ?? (options.success ? 200 : 500),
                properties: options.properties,
            });
            console.log(`✅ Dependency tracked: ${options.name}`);
        } catch (error) {
            console.error('❌ Failed to track dependency:', error);
        }
    }

    setAuthenticatedUserContext(userId, accountId) {
        if (!this.isInitialized || !this.appInsights) {
            console.warn('Application Insights not initialized. User context not set');
            return;
        }

        try {
            this.appInsights.setAuthenticatedUserContext(userId, accountId);
            console.log(`✅ User context set: ${userId}`);
        } catch (error) {
            console.error('❌ Failed to set user context:', error);
        }
    }

    clearAuthenticatedUserContext() {
        if (!this.isInitialized || !this.appInsights) {
            console.warn('Application Insights not initialized. User context not cleared');
            return;
        }

        try {
            this.appInsights.clearAuthenticatedUserContext();
            console.log('✅ User context cleared');
        } catch (error) {
            console.error('❌ Failed to clear user context:', error);
        }
    }

    async flush() {
        if (!this.isInitialized || !this.appInsights) {
            return;
        }

        try {
            this.appInsights.flush();
            console.log('✅ Telemetry flushed');
        } catch (error) {
            console.error('❌ Failed to flush telemetry:', error);
        }
    }

    getIsInitialized() {
        return this.isInitialized;
    }
}

const appInsightsService = new AppInsightsService();
export default appInsightsService;